import React, { useMemo } from 'react'
import { getTime, startOfDay, endOfDay } from 'date-fns'
import { omitBy, isUndefined } from '@golden/utils'
import { ActivityType, ActivityNewYearStatusType } from '@golden/gdk-admin'
import Grid from '@material-ui/core/Grid'
import MuiTextField, { TextFieldProps } from '@material-ui/core/TextField'
import FormField from '../../default/form/FormField'
import DropDown, { PropTypes as DropDownProps } from '../../default/form/DropDown'
import { FormState, ValueGetter } from '../../../utils/default/FormHook'
import { ActivityFormType, ActivityRequest } from './ActivityRecordDetailForm'
import useT from '../../../i18ns/admin/useT'
import { getValueFromChangeEvent, convertEmptyToUndefined, acceptUndefined, pipe, guaranteeBeKey } from '../../../utils/default/FormHelper'
import { SearchToRequestFunc } from '../../../utils/default/ComplexFlowHook'
import { newYearDrawStatusName } from '../../../constants/default/activityStatusName'

export interface ActivityNewYearDrawFormType {
  account: string
  agent_account: string
  newYear2026DrawStatus: ActivityNewYearStatusType | '--'
  serial_number: string
  draw_serial_number: string
}

export const initialForm = (): ActivityNewYearDrawFormType => ({
  account: '',
  agent_account: '',
  newYear2026DrawStatus: '--',
  serial_number: '',
  draw_serial_number: ''
})

export const getValueFromEvent: ValueGetter<ActivityNewYearDrawFormType> = {
  account: getValueFromChangeEvent,
  agent_account: getValueFromChangeEvent,
  newYear2026DrawStatus: getValueFromChangeEvent,
  serial_number: getValueFromChangeEvent,
  draw_serial_number: getValueFromChangeEvent
}

export const formToRequest = (form: ActivityFormType): ActivityRequest => {
  const converted = {
    activity: ActivityType.NEW_YEAR_2026_TICKET,
    account: convertEmptyToUndefined(form.account),
    agent_account: convertEmptyToUndefined(form.agent_account),
    status: form.newYear2026DrawStatus === '--' ? undefined : form.newYear2026DrawStatus,
    serial_number: convertEmptyToUndefined(form.serial_number),
    draw_serial_number: convertEmptyToUndefined(form.draw_serial_number),
    type: form.type,
    start_at: form.time.start === null ? startOfDay(new Date()).getTime() : getTime(form.time.start),
    end_at: form.time.end === null ? endOfDay(new Date()).getTime() : getTime(form.time.end),
    page: 1
  } as ActivityRequest
  return omitBy(converted, isUndefined) as ActivityRequest
}

export const searchToRequest: SearchToRequestFunc<{
  account: string | undefined
  agent_account: string | undefined
  status: string | undefined
  serial_number: string | undefined
  draw_serial_number: string | undefined
}> = (search) => {
  const converted = {
    account: search.account,
    agent_account: search.agent_account,
    newYear2026DrawStatus: acceptUndefined(search.status, pipe(
      (value) => guaranteeBeKey(value, Object.keys(newYearDrawStatusName))
    )),
    serial_number: search.serial_number,
    draw_serial_number: search.draw_serial_number
  }
  return omitBy(converted, isUndefined) as any
}

interface PropTypes {
  context: React.Context<FormState<ActivityFormType>>
}

const TextField = React.memo(MuiTextField)

const ActivityNewYear2026DrawForm: React.FC<PropTypes> = (props) => {
  const { context } = props
  const { t } = useT()

  const stateOptions = useMemo(() => {
    return [{ name: t('common.all'), value: '--' }]
      .concat(
        Object.keys(newYearDrawStatusName)
          .map((key) => ({ name: t(newYearDrawStatusName[key as ActivityNewYearStatusType]), value: key }))
      )
  }, [t])

  return (
    <React.Fragment>
      <Grid item xs={12} md={6} lg={3}>
        <FormField<ActivityFormType, TextFieldProps>
          context={context}
          component={TextField}
          name="account"
          label={t('common.playerAccount')}
          placeholder={t('placeholder.inputPlayerAccount')}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <FormField<ActivityFormType, TextFieldProps>
          context={context}
          component={TextField}
          name="agent_account"
          label={t('common.agentAccount')}
          placeholder={t('placeholder.inputAgentAccount')}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <FormField<ActivityFormType, DropDownProps>
          context={context}
          component={DropDown}
          name="newYear2026DrawStatus"
          label={t('common.status')}
          options={stateOptions}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <FormField<ActivityFormType, TextFieldProps>
          context={context}
          component={TextField}
          name="serial_number"
          label={t('common.activitySerial')}
          placeholder={t('placeholder.inputActivitySerial')}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <FormField<ActivityFormType, TextFieldProps>
          context={context}
          component={TextField}
          name="draw_serial_number"
          label={t('common.drawSerialNumber')}
          placeholder={t('placeholder.inputDrawSerialNumber')}
          fullWidth
        />
      </Grid>
    </React.Fragment>
  )
}

export default React.memo(ActivityNewYear2026DrawForm)
